import React from 'react';
import {
    BarChart3,
    BookOpen,
    Compass,
    Plus,
    Route,
    type LucideIcon,
} from 'lucide-react';
import type { TranslationKeys } from '../../translations';

export const ALL_TAB_IDS = [
    'flights',
    'carTrips',
    'journeys',
    'planning',
    'buses',
    'trains',
    'ferries',
    'hotels',
    'analytics',
] as const;

export type TabId = (typeof ALL_TAB_IDS)[number];

/** Modes under the Trips umbrella (transport + hotels). */
export const TRIPS_TAB_IDS = [
    'flights',
    'carTrips',
    'buses',
    'trains',
    'ferries',
    'hotels',
] as const;
export type TripsTabId = (typeof TRIPS_TAB_IDS)[number];

const TRIPS_MODE_KEY = 'mytraveldiary.transportMode';

export function isTripsTab(id: TabId): id is TripsTabId {
    return (TRIPS_TAB_IDS as readonly string[]).includes(id);
}

export function readLastTransportMode(): TripsTabId {
    try {
        const v = localStorage.getItem(TRIPS_MODE_KEY) || '';
        if ((TRIPS_TAB_IDS as readonly string[]).includes(v)) {
            return v as TripsTabId;
        }
    } catch {
        /* ignore */
    }
    return 'flights';
}

export function writeLastTransportMode(id: TripsTabId): void {
    try {
        localStorage.setItem(TRIPS_MODE_KEY, id);
    } catch {
        /* ignore */
    }
}

type DesktopTabItem = {
    key: string;
    label: string;
    icon: LucideIcon;
    activeCls: string;
    isActive: (activeTab: TabId) => boolean;
    onSelect: () => void;
};

type MobileNavItem = {
    key: string;
    label: string;
    icon: LucideIcon;
    activeCls: string;
    isActive: (activeTab: TabId) => boolean;
    onSelect: () => void;
};

function buildPrimaryNav(
    t: TranslationKeys,
    language: string,
    onTabChange: (id: TabId) => void,
): DesktopTabItem[] {
    const ru = language?.startsWith('ru');
    return [
        {
            key: 'journeys',
            label: t.journeys,
            icon: BookOpen,
            activeCls: 'from-sky-500 to-sky-700 shadow-sky-600/30',
            isActive: tab => tab === 'journeys',
            onSelect: () => onTabChange('journeys'),
        },
        {
            key: 'planning',
            label: ru ? 'План' : 'Plan',
            icon: Compass,
            activeCls: 'from-blue-500 to-blue-700 shadow-blue-600/30',
            isActive: tab => tab === 'planning',
            onSelect: () => onTabChange('planning'),
        },
        {
            key: 'trips',
            label: t.trips,
            icon: Route,
            activeCls: 'from-amber-500 to-orange-600 shadow-amber-500/30',
            isActive: isTripsTab,
            onSelect: () => onTabChange(readLastTransportMode()),
        },
        {
            key: 'analytics',
            label: ru ? 'Стат.' : 'Stats',
            icon: BarChart3,
            activeCls: 'from-fuchsia-500 to-pink-600 shadow-fuchsia-500/30',
            isActive: tab => tab === 'analytics',
            onSelect: () => onTabChange('analytics'),
        },
    ];
}

type TabNavProps = {
    variant: 'desktop' | 'mobile';
    activeTab: TabId;
    onTabChange: (id: TabId) => void;
    language: string;
    t: TranslationKeys;
    /** When trips is active, middle slot becomes Add and calls this. */
    onAddTransport?: () => void;
};

export const TabNav: React.FC<TabNavProps> = ({
    variant,
    activeTab,
    onTabChange,
    language,
    t,
    onAddTransport,
}) => {
    const tabs = buildPrimaryNav(t, language, onTabChange);

    if (variant === 'desktop') {
        return (
            <nav
                className="hidden md:block border-b border-stone-200/80 dark:border-slate-700/60 bg-gradient-to-b from-white/70 to-stone-50/50 dark:from-slate-800/50 dark:to-slate-900/40 px-3 lg:px-4"
                aria-label="Tabs"
            >
                <div className="flex flex-wrap items-center justify-center gap-2 py-3">
                    {tabs.map(tab => {
                        const on = tab.isActive(activeTab);
                        return (
                            <button
                                key={tab.key}
                                type="button"
                                onClick={tab.onSelect}
                                className={`group flex flex-col items-center justify-center gap-1 min-w-[4.25rem] sm:min-w-[5.5rem] px-2.5 py-2.5 text-[12px] font-semibold rounded-2xl border transition-all duration-200 ${
                                    on
                                        ? `bg-gradient-to-br ${tab.activeCls} text-white border-transparent shadow-md`
                                        : 'bg-white dark:bg-slate-800/90 text-stone-700 dark:text-slate-200 border-stone-200/90 dark:border-slate-600 hover:border-stone-300 dark:hover:border-slate-500 hover:shadow-sm'
                                }`}
                            >
                                <tab.icon
                                    className={`h-5 w-5 shrink-0 ${on ? 'text-white' : 'text-stone-500 dark:text-slate-400'}`}
                                />
                                <span className="leading-tight text-center">{tab.label}</span>
                            </button>
                        );
                    })}
                </div>
            </nav>
        );
    }

    const ru = language?.startsWith('ru');
    const addLabel = ru ? 'Добавить' : 'Add';
    const tripsAddCls =
        activeTab === 'carTrips'
            ? 'from-amber-500 to-orange-600'
            : activeTab === 'buses'
              ? 'from-violet-500 to-purple-600'
              : activeTab === 'trains'
                ? 'from-indigo-500 to-slate-700'
                : activeTab === 'ferries'
                  ? 'from-cyan-500 to-teal-600'
                  : activeTab === 'hotels'
                    ? 'from-teal-500 to-teal-700'
                    : 'from-sky-500 to-cyan-600';

    return (
        <nav
            className="fixed bottom-0 left-0 right-0 z-30 md:hidden border-t border-white/20 dark:border-slate-700/50 bg-white/90 dark:bg-slate-900/95 backdrop-blur-xl safe-area-padding-bottom"
            aria-label="Mobile navigation"
        >
            <div className="flex items-stretch justify-between gap-0.5 py-1 px-1">
                {tabs.map(tab => {
                    const on = tab.isActive(activeTab);
                    if (tab.key === 'trips' && on && onAddTransport) {
                        return (
                            <button
                                key={tab.key}
                                type="button"
                                onClick={onAddTransport}
                                className={`flex flex-1 flex-col items-center justify-center gap-0.5 min-w-0 min-h-11 py-1.5 px-0.5 rounded-xl transition-colors bg-gradient-to-br ${tripsAddCls} text-white shadow-sm`}
                                title={addLabel}
                                aria-label={addLabel}
                            >
                                <Plus className="h-5 w-5 shrink-0" strokeWidth={2.5} />
                            </button>
                        );
                    }
                    return (
                        <button
                            key={tab.key}
                            type="button"
                            onClick={tab.onSelect}
                            className={`flex flex-1 flex-col items-center justify-center gap-0.5 min-w-0 min-h-11 py-1.5 px-0.5 rounded-xl transition-colors ${
                                on
                                    ? `bg-gradient-to-br ${tab.activeCls} text-white shadow-sm`
                                    : 'text-stone-500 dark:text-slate-400'
                            }`}
                        >
                            <tab.icon className={`h-5 w-5 shrink-0 ${on ? 'text-white' : ''}`} />
                            <span
                                className={`text-[10px] font-medium leading-tight truncate max-w-full ${on ? 'text-white' : ''}`}
                            >
                                {tab.label}
                            </span>
                        </button>
                    );
                })}
            </div>
        </nav>
    );
};
